import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import categoriesUtils from '../../../utils/categories.utils';
import api from '../../../middleware/api';
import TagInput from '../../Common/Inputs/TagInput/TagInput';
import BasicInput from '../../Common/Inputs/BasicInput/BasicInput';
import Dropdown from '../../Common/Dropdown/Dropdown';
import useFetchData from '../../../hooks/useFetchData';
import { toast } from 'react-toastify';
import mixturesSchema from '../../../schema/mixtures.schema';
import TextareaInput from '../../Common/Inputs/TextareaInput/TextareaInput';
import { useTable } from '../../../context/TableContext';
import { formatMixtureRef } from '../../../utils/format.utils';
import { PlusCircleIcon, BeakerIcon, TagIcon, DocumentTextIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { TabletsIcon } from 'lucide-react';
import LoadingSpinner from '../../Common/Loading/LoadingSpinner';
import { useAuth } from '../../../context/AuthContext';
import { ROLES } from '../../../utils/roles.utils';

/**
 * Composant réutilisable pour la création et mise à jour de mélanges
 * @param {Object} props - Propriétés du composant
 * @param {string} props.mode - Mode du formulaire ('create' ou 'update')
 * @param {Function} props.onClose - Fonction pour fermer le drawer
 * @param {Function} props.setTableData - Fonction pour mettre à jour les données du tableau (mode create)
 * @param {Object} props.mixture - Données du mélange à modifier (mode update)
 * @param {number} props.rowIndex - Index de la ligne à modifier (mode update)
 * @param {Object} props.table - Référence à la table (mode update)
 */
const MixtureForm = ({ mode = 'create', onClose, setTableData, mixture, rowIndex, table }) => {
    const { user } = useAuth();
    const { API_URL } = useTable();
    const version = API_URL && API_URL.includes('custom') ? 'custom' : 'catalogue';

    // Initialisation avec null, on le mettra à jour après l'initialisation de formik
    const [mixturesCountUrl, setMixturesCountUrl] = useState(null);
    const [isSubCategorySelected, setIsSubCategorySelected] = useState(false);

    const dilutions = useFetchData('/dilutions');
    const mixturesCount = useFetchData(mixturesCountUrl);

    const formatCatalogueRef = (ref) => {
        // Return only number in ref
        const number = parseInt(ref.replace(/[^0-9]/g, ''));
        if (isNaN(number)) return ref.replace(/[^0-9]/g, '');

        if (number < 10) return String('0' + number);
        return String(number);
    };

    const getInitialValues = () => {
        if (mode === 'create') {
            return {
                name: '',
                reagents: [],
                category: '',
                subCategory: '',
                dilution: '',
                ref: '',
                description: ''
            };
        } else {
            return {
                name: mixture.name,
                reagents: mixture.reagents,
                category: mixture.category,
                subCategory: mixture.subCategory,
                dilution: mixture.defaultDilution.label,
                ref: version === 'custom' ? mixture.ref : formatCatalogueRef(mixture.ref),
                description: mixture.description || ''
            };
        }
    };

    const handleMixtureCreation = async (values) => {
        try {
            // Vérification que tous les champs sont bien remplis
            if (!values.category || !values.subCategory || !values.dilution) {
                const message = 'Veuillez remplir tous les champs de catégorisation';
                formik.setSubmitting(false);
                formik.setErrors({ submit: message });
                return;
            }

            const dilution = dilutions.data.values.find(dilution => dilution.label === values.dilution);
            if (!dilution) {
                const message = 'Veuillez sélectionner une dilution valide';
                formik.setSubmitting(false);
                formik.setErrors({ submit: message });
                return;
            }

            const { dilution: _dilutionLabel, ...cleanValues } = values;
            const response = await api.post(`/mixtures/${version}/create`, { ...cleanValues, dilutionId: dilution.id });
            if (response.status === 200) {
                onClose();
                setTableData((prev) => {
                    if ((!prev || prev?.values.length === 0)) {
                        return {
                            pagination: { page: 0, pageCount: 1, total: 1, pageSize: 10, max_per_query: 100 },
                            values: [response.data]
                        };
                    } else {
                        return {
                            ...prev,
                            values: [response.data, ...prev?.values]
                        };
                    }
                });
                toast.success('Mélange créé avec succès', { autoClose: 1000 });
                formik.resetForm();
            }
        } catch (err) {
            console.error(err);
            const message = err.response?.data?.message || 'Erreur lors de la création du mélange';
            formik.setSubmitting(false);
            formik.setErrors({ submit: message });
        }
    };

    const handleMixtureUpdate = async (values) => {
        try {
            // Vérification que tous les champs sont bien remplis
            if (!values.category || !values.subCategory || !values.dilution) {
                const message = 'Veuillez remplir tous les champs de catégorisation';
                formik.setSubmitting(false);
                formik.setErrors({ submit: message });
                return;
            }

            const dilution = dilutions.data.values.find(dilution => dilution.label === values.dilution);
            if (!dilution) {
                const message = 'Veuillez sélectionner une dilution valide';
                formik.setSubmitting(false);
                formik.setErrors({ submit: message });
                return;
            }

            const { dilution: _dilutionLabel, ...cleanValues } = values;
            const response = await api.put(`/mixtures/${version}/update/${mixture.id}`, { ...cleanValues, dilutionId: dilution.id });
            if (response.status === 200) {
                table.options.meta?.updateFullRow(rowIndex, response.data);
                toast.success('Mélange mis à jour !', { autoClose: 1000 });
                onClose();
                formik.resetForm();
            }
        } catch (err) {
            console.error(err);
            const message = err.response?.data?.message || 'Erreur lors de la mise à jour du mélange';
            formik.setSubmitting(false);
            formik.setErrors({ submit: message });
        }
    };

    const handlePrincipalCategoryChange = (e) => {
        formik.setFieldValue('category', e.target.value);
        if (e.target.value) {
            const subCategories = categoriesUtils.getCategoriesByParent(e.target.value);
            if (subCategories && subCategories.length > 0) {
                formik.setFieldValue('subCategory', subCategories[0].label);
                setIsSubCategorySelected(true);
            } else {
                formik.setFieldValue('subCategory', '');
                setIsSubCategorySelected(false);
            }
        } else {
            formik.setFieldValue('subCategory', '');
            setIsSubCategorySelected(false);
        }
    };

    const handleSubCategoryChange = (e) => {
        formik.setFieldValue('subCategory', e.target.value);
        setIsSubCategorySelected(!!e.target.value);
    };

    const handleRefChange = (e) => {
        const value = parseInt(e.target.value);
        if (isNaN(value)) return;
        formik.setFieldValue('ref', value);
    };

    const formik = useFormik({
        initialValues: getInitialValues(),
        validationSchema: version === 'custom' ? mixturesSchema.createCustomMixture : mixturesSchema.createCatalogueMixture,
        onSubmit: async (values) => {
            if (mode === 'create') {
                if (user?.permission?.label !== ROLES.ADMIN.label && version === 'catalogue') {
                    toast.error('Vous n\'avez pas les permissions pour créer un mélange catalogue', { autoClose: 1000 });
                    return;
                }

                if (version === 'custom') {
                    const ref = String(values.ref);
                    const newMixture = { ...values, ref };
                    await handleMixtureCreation(newMixture);
                } else {
                    const ref = categoriesUtils.getRefFromCategory(formik.values.subCategory) + '-' + formatMixtureRef(formik.values.ref);
                    const newMixture = { ...values, ref };
                    await handleMixtureCreation(newMixture);
                }
            } else {
                if (version === 'custom') {
                    await handleMixtureUpdate(values);
                } else {
                    const ref = categoriesUtils.getRefFromCategory(values.subCategory) + '-' + formatMixtureRef(formik.values.ref);
                    await handleMixtureUpdate({ ...values, ref });
                }
            }
        }
    });

    // Effet pour mettre à jour l'URL de comptage lorsque la sous-catégorie change
    useEffect(() => {
        if (mode === 'create' && formik.values.subCategory) {
            const newUrl = `/mixtures/${version}/count/${formik.values.subCategory}`;
            setMixturesCountUrl(newUrl);
        } else if (mode === 'create' && !formik.values.subCategory) {
            setMixturesCountUrl(null);  // Réinitialiser l'URL si aucune sous-catégorie n'est sélectionnée
        }
    }, [formik.values.subCategory, mode, version]);

    // Effet pour mettre à jour la référence lorsque le comptage change
    useEffect(() => {
        if (mode === 'create' && mixturesCount?.data) {
            const value = mixturesCount.data.count + 1;
            if (value < 10) {
                formik.setFieldValue('ref', String('0' + value));
            } else {
                formik.setFieldValue('ref', String(value));
            }
        }
    }, [mixturesCount?.data, mixturesCountUrl, mode]);

    // Pour les cas de mise à jour, initialise l'état isSubCategorySelected
    useEffect(() => {
        if (mode === 'update' && mixture?.subCategory) {
            setIsSubCategorySelected(true);
        }
    }, [mode, mixture]);

    if (dilutions.isLoading) {
        return <div className="flex justify-center items-center h-full w-full p-8">
            <LoadingSpinner />
        </div>;
    }

    if (!dilutions.isLoading && dilutions.data.values.length <= 0) {
        return <div className="bg-amber-50 text-amber-800 p-4 rounded-lg shadow-sm">
            <p className="flex items-center gap-2">
                <BeakerIcon className="h-5 w-5" />
                Aucune dilution disponible
            </p>
        </div>;
    }

    return (
        <form className='flex flex-col w-full gap-6 mx-auto max-w-7xl px-4 py-2' onSubmit={formik.handleSubmit}>
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                    <BeakerIcon className="h-5 w-5 text-primary-600" />
                    {mode === 'create' ? 'Créer un nouveau mélange' : 'Modifier le mélange'}
                    <span className="text-xs font-normal px-2 py-1 bg-primary/10 text-primary rounded-full">
                        {version === 'custom' ? 'Personnalisé' : 'Catalogue'}
                    </span>
                </h3>

                <div className="grid gap-4 md:grid-cols-3">
                    <div className="md:col-span-2">
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Nom du mélange</label>
                        <BasicInput
                            id='name'
                            name='name'
                            type='text'
                            placeholder='ex: Mélange H2O + NaCl'
                            onChange={formik.handleChange}
                            value={formik.values.name}
                            error={formik.errors?.name ? formik.errors.name : null}
                            touched={formik.touched?.name ? formik.touched.name : null}
                            className="focus:ring-primary-500 focus:border-primary-500"
                        />
                    </div>
                    <div>
                        <label htmlFor="ref" className="block text-sm font-medium text-gray-700 mb-1">Référence</label>
                        {version === 'custom' &&
                            <BasicInput
                                id='ref'
                                name='ref'
                                type={mode === 'create' ? 'number' : 'text'}
                                placeholder='ex: 1234'
                                onChange={formik.handleChange}
                                value={formik.values.ref}
                                error={formik.errors?.ref ? formik.errors.ref : null}
                                touched={formik.touched?.ref ? formik.touched.ref : null}
                                className="focus:ring-primary-500 focus:border-primary-500"
                            />}

                        {version === 'catalogue' &&
                            <div className="flex items-center">
                                <span className="inline-flex items-center px-3 text-gray-500 bg-gray-100 rounded-l-md border border-r-0 border-gray-300">
                                    {formik.values.subCategory ? categoriesUtils.getRefFromCategory(formik.values.subCategory) : ''} -
                                </span>
                                <BasicInput
                                    className="rounded-l-none focus:ring-primary-500 focus:border-primary-500"
                                    min={0}
                                    required
                                    id='ref'
                                    name='ref'
                                    type='number'
                                    placeholder='00'
                                    onChange={handleRefChange}
                                    value={formik.values.ref}
                                    error={formik.errors?.ref ? formik.errors.ref : null}
                                    touched={formik.touched?.ref ? formik.touched.ref : null}
                                    disabled={!isSubCategorySelected}
                                />
                            </div>
                        }
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                    <TagIcon className="h-5 w-5 text-primary-600" />
                    Catégorisation
                </h3>

                <div className="grid gap-4 md:grid-cols-3">
                    <div>
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">Catégorie principale</label>
                        <Dropdown
                            id='category'
                            name='category'
                            onChange={handlePrincipalCategoryChange}
                            value={formik.values.category}
                            options={categoriesUtils.getParentCategories()}
                            error={formik.errors?.category ? formik.errors.category : null}
                            touched={formik.touched?.category ? formik.touched.category : null}
                            className="focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Sélectionnez une catégorie"
                        />
                    </div>
                    <div>
                        <label htmlFor="subCategory" className="block text-sm font-medium text-gray-700 mb-1">Sous-catégorie</label>
                        <Dropdown
                            id='subCategory'
                            name='subCategory'
                            onChange={handleSubCategoryChange}
                            value={formik.values.subCategory}
                            options={formik.values.category ? categoriesUtils.getCategoriesByParent(formik.values.category).map(category => category.label) : []}
                            error={formik.errors?.subCategory ? formik.errors.subCategory : null}
                            touched={formik.touched?.subCategory ? formik.touched.subCategory : null}
                            className="focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Sélectionnez une sous-catégorie"
                            disabled={!formik.values.category}
                        />
                    </div>
                    <div>
                        <label htmlFor="dilution" className="block text-sm font-medium text-gray-700 mb-1">Dilution</label>
                        <Dropdown
                            id='dilution'
                            name='dilution'
                            onChange={formik.handleChange}
                            value={formik.values.dilution}
                            options={dilutions.data.values.map((dilution) => dilution.label)}
                            error={formik.errors?.dilution ? formik.errors.dilution : null}
                            touched={formik.touched?.dilution ? formik.touched.dilution : null}
                            className="focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Sélectionnez une dilution"
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                    <ArchiveBoxIcon className="h-5 w-5 text-primary-600" />
                    Composition et détails
                </h3>

                <div className="space-y-4">
                    <div>
                        <label htmlFor="reagents" className=" ml-1 text-sm font-medium text-gray-700 mb-1 flex items-center gap-1 flex w-full justify-between">
                            <span className='flex items-center gap-1'>
                                <TabletsIcon className="h-4 w-4" />
                                Réactifs
                            </span>
                            <p className="text-xs text-gray-500">Réactifs trouvés : {formik.values.reagents.length}</p>
                        </label>
                        <TagInput
                            id="reagents"
                            className="focus:ring-primary-500 focus:border-primary-500"
                            formik={formik}
                            field="reagents"
                            placeholder={mode === 'create'
                                ? "Ajoutez un ou plusieurs reactifs. Séparez les appuyant sur la touche 'Entrée'"
                                : "Ajoutez un réactif"}
                            icon={mode === 'update' ? PlusCircleIcon : undefined}
                        />

                    </div>

                    <div>
                        <label htmlFor="description" className="ml-1 text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                            <DocumentTextIcon className="h-4 w-4" />
                            Description
                        </label>
                        <TextareaInput
                            id="description"
                            name="description"
                            placeholder="Ajoutez une description détaillée du mélange..."
                            onChange={formik.handleChange}
                            value={formik.values.description || ''}
                            error={formik.errors?.description ? formik.errors.description : null}
                            touched={formik.touched?.description ? formik.touched.description : null}
                            className="focus:ring-primary-500 focus:border-primary-500 min-h-[120px]"
                        />
                    </div>
                </div>
            </div>

            {formik.errors.submit && (
                <div className="bg-red-50 text-red-600 p-4 rounded-lg shadow-sm flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    {formik.errors.submit}
                </div>
            )}

            <div className="flex justify-center mt-2">
                <button
                    className={`btn btn-primary px-8 py-2 transition-all duration-200 flex items-center gap-2 ${formik.isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-md'}`}
                    type="submit"
                    disabled={formik.isSubmitting}
                >
                    {formik.isSubmitting && (
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    )}
                    {mode === 'create' ? 'Créer le mélange' : 'Mettre à jour'}
                </button>
            </div>
        </form>
    );
};

export default MixtureForm; 